/**
 * Text normalization and entity matching helper for HITL Review source documents.
 */

export function normalizeText(str = '') {
  return (str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
}

/**
 * Checks if a clicked or detected text string is document-level boilerplate
 * (such as state form title banners, agency headers, statutory citations, or line-wrapped fragments)
 * that should not trigger entity matching or field-level action popovers.
 */
export function isDocumentBoilerplateText(text = '') {
  if (!text) return true
  const clean = text.replace(/[’‘`]/g, "'").trim()
  if (clean.length < 2) return true

  // Form titles & official state agency banners (handles full titles and arbitrary line-wrapped segments)
  if (
    /^(?:workers'?\s+compensation(?:\s+first)?|first\s+report(?:\s+of\s+injury(?:\s+or\s+illness)?)?|report\s+of\s+injury(?:\s+or\s+illness)?|of\s+injury(?:\s+or\s+illness)?|injury\s+or\s+illness|employer'?s\s+(?:first\s+)?report(?:\s+of\s+injury(?:\s+or\s+illness)?)?|division\s+of\s+workers'?\s+compensation|colorado\s+department|department\s+of\s+labor|state\s+of\s+colorado|state\s+of\s+[a-z]+)\b/i.test(
      clean,
    )
  ) {
    return true
  }

  // Statutory citations and disclaimer boilerplates
  if (
    /^(?:c\.?r\.?s\.?|revised\s+statutes|form\s+[a-z0-9-]+|page\s+\d+\s+of\s+\d+|general\s+instructions|important\s+notice)(?:[\s:;.,-]|\b|$)/i.test(
      clean,
    )
  ) {
    return true
  }

  return false
}

/**
 * Finds the best matching entity from a clicked or selected text snippet.
 * Checks against:
 * 1. Field name/title (e.g. "Date of Injury", "Insured Name", "Employer", etc.)
 * 2. Extracted value (e.g. "10/29/2025", "Acme Corp", "Marcus Brody, MD")
 * 3. Attributes (e.g. attributes.name, attributes.address, attributes.description, etc.)
 * 4. Source snippet
 * 5. Section context (e.g. "insured", "claimant", "provider", "injury", "referral")
 * 6. Common domain synonyms (e.g. "Insured Name and Address", "DOI", "Date of Accident", "Treating Physician", etc.)
 *
 * @param {string} rawText The clicked or selected text from the document
 * @param {Array} entities The list of extracted review entities
 * @param {object} [options] Optional match options (e.g. { sectionContext: 'insured' })
 * @returns {object|null} The matched entity or null
 */
export function findMatchingEntity(rawText, entities = [], options = {}) {
  if (!rawText || !Array.isArray(entities) || entities.length === 0) return null

  // Reject document-level titles, banners, and statutory boilerplate
  if (isDocumentBoilerplateText(rawText)) {
    return null
  }

  const cleanText = normalizeText(rawText)
  if (cleanText.length < 2) return null

  if (isDocumentBoilerplateText(cleanText)) {
    return null
  }

  const sectionContext = options?.sectionContext ? normalizeText(options.sectionContext) : null
  const isGenericFieldLabel =
    /^(?:name|main address|address|phone|telephone|fax|fein|ssn|date|city|state|zip|gender|dob|description)$/i.test(
      cleanText,
    )

  let bestMatch = null
  let highestScore = 0

  for (const ent of entities) {
    if (!ent) continue

    const field = normalizeText(ent.field || ent.fieldTitle || ent.fieldLabel || ent.label || '')
    const val = normalizeText(ent.extractedValue || '')
    const snippet = normalizeText(ent.sourceSnippet || '')
    const category = normalizeText(ent.category || '')

    let score = 0

    // Section relevance assessment
    let isMatchingSection = false
    let isConflictingSection = false
    if (sectionContext) {
      if (sectionContext.includes('insured') || sectionContext.includes('employer')) {
        isMatchingSection =
          category.includes('insured') ||
          category.includes('employer') ||
          field.includes('insured') ||
          field.includes('employer')
        isConflictingSection =
          !isMatchingSection &&
          (category.includes('provider') || field.includes('claimant') || category.includes('claimant'))
      } else if (
        sectionContext.includes('claimant') ||
        sectionContext.includes('employee') ||
        sectionContext.includes('patient')
      ) {
        isMatchingSection =
          category.includes('demographics') ||
          category.includes('claimant') ||
          category.includes('patient') ||
          field.includes('claimant') ||
          field.includes('employee')
        isConflictingSection =
          !isMatchingSection &&
          (category.includes('insured') || category.includes('provider') || field.includes('insured'))
      } else if (sectionContext.includes('provider') || sectionContext.includes('physician')) {
        isMatchingSection =
          category.includes('provider') ||
          field.includes('provider') ||
          field.includes('physician') ||
          field.includes('doctor')
        isConflictingSection = !isMatchingSection && (category.includes('insured') || field.includes('insured'))
      } else if (sectionContext.includes('injury') || sectionContext.includes('illness')) {
        isMatchingSection =
          category.includes('diagnosis') ||
          category.includes('injury') ||
          field.includes('injury') ||
          field.includes('diagnosis')
      } else if (
        sectionContext.includes('referral') ||
        sectionContext.includes('claim') ||
        sectionContext.includes('carrier')
      ) {
        isMatchingSection =
          category.includes('referral') ||
          category.includes('claim') ||
          field.includes('claim') ||
          field.includes('carrier') ||
          field.includes('policy')
      }
    }

    // Section conflict guard:
    // If the click occurs within a known form section (e.g. "Insured Information"),
    // entities from conflicting sections (e.g. Medical Provider or Claimant) must never match
    // via generic labels, address leakage, attributes, or snippet containment.
    if (isConflictingSection) {
      if (val && cleanText === val) {
        score = 98
      } else {
        continue
      }
    }

    // Generic label disambiguation (e.g. "Name", "Main Address", "Phone"):
    // Generic labels MUST be disambiguated by sectionContext and should never jump cross-section.
    if (isGenericFieldLabel) {
      if (isMatchingSection) {
        if (cleanText === 'name') {
          if (field.includes('name')) {
            score = Math.max(score, 96)
          } else if (category.includes('provider') || field.includes('employer')) {
            score = Math.max(score, 94)
          }
        } else if (cleanText.includes('address')) {
          if (field.includes('address')) {
            score = Math.max(score, 96)
          } else if (ent.attributes?.address || ent.attributes?.street || ent.attributes?.city) {
            score = Math.max(score, 95)
          } else if (field.includes('employer') || category.includes('employer')) {
            score = Math.max(score, 94)
          }
        } else if (
          cleanText.includes('phone') ||
          cleanText.includes('telephone') ||
          cleanText.includes('tel') ||
          cleanText.includes('cell')
        ) {
          if (field.includes('phone') || field.includes('cell') || field.includes('fax')) {
            score = Math.max(score, 96)
          } else if (ent.attributes?.phone || ent.attributes?.telephone || ent.attributes?.cell) {
            score = Math.max(score, 95)
          } else if (field.includes('employer') || category.includes('employer')) {
            score = Math.max(score, 92)
          }
        } else if (cleanText.includes('date') && field.includes('date')) {
          score = Math.max(score, 92)
        } else if (field.includes(cleanText)) {
          score = Math.max(score, 90)
        }
      } else {
        // Without a matching section context, generic labels stay below match threshold
        score = Math.max(score, 50)
      }
    }

    // Exact matches
    if (field && cleanText === field) {
      score = Math.max(score, 100)
    }
    if (val && val.length >= 3 && cleanText === val) {
      score = Math.max(score, 98)
    }

    // Direct containment: clicked text contains field or field contains clicked text (for non-generic labels)
    if (!isGenericFieldLabel && field && field.length >= 3) {
      if (cleanText === field) {
        score = Math.max(score, 100)
      } else if (cleanText.includes(field)) {
        // High confidence only if the field is a substantial portion of the clicked text
        // (prevents long form titles or sentences from matching a single-word field)
        const lengthRatio = field.length / cleanText.length
        if (lengthRatio >= 0.4 || cleanText.length <= field.length + 10) {
          score = Math.max(score, 92)
        } else {
          score = Math.max(score, 45)
        }
      } else if (field.includes(cleanText) && cleanText.length >= 4) {
        // Prepositional fragments (e.g. "of injury", "for injury") do not match
        const isPreposition = /^(?:of|for|in|to|the|and|by|at|from)\b/i.test(cleanText)
        const lengthRatio = cleanText.length / field.length
        if (!isPreposition && lengthRatio >= 0.5) {
          score = Math.max(score, 82)
        } else {
          score = Math.max(score, 40)
        }
      }
    }

    // Direct containment: clicked text contains extracted value or vice versa
    if (val && val.length >= 3) {
      if (cleanText === val) {
        score = Math.max(score, 98)
      } else if (cleanText.includes(val)) {
        const ratio = val.length / cleanText.length
        if (ratio >= 0.5 || cleanText.length <= val.length + 8) {
          score = Math.max(score, 88)
        } else {
          score = Math.max(score, 45)
        }
      } else if (val.includes(cleanText) && cleanText.length >= 5) {
        const ratio = cleanText.length / val.length
        if (ratio >= 0.6 && cleanText.length >= 6) {
          score = Math.max(score, 82)
        } else {
          score = Math.max(score, 40)
        }
      }
    }

    // Attributes containment
    if (ent.attributes && typeof ent.attributes === 'object') {
      for (const attrVal of Object.values(ent.attributes)) {
        if (typeof attrVal === 'string' && attrVal.trim().length >= 3) {
          const cleanAttr = normalizeText(attrVal)
          if (cleanText === cleanAttr) {
            score = Math.max(score, 90)
          } else if (cleanText.includes(cleanAttr) && cleanAttr.length >= 5) {
            score = Math.max(score, 80)
          } else if (
            cleanAttr.includes(cleanText) &&
            cleanText.length >= 6 &&
            cleanText.length / cleanAttr.length >= 0.6
          ) {
            score = Math.max(score, 75)
          }
        }
      }
    }

    // Snippet containment:
    // If the clicked/selected text contains the full entity sourceSnippet, that is a solid match (75).
    // If the clicked text is merely a substring within the snippet, it must have substantive overlap
    // with the entity's extracted value or field name to prevent unrelated surrounding narrative from matching.
    if (!isGenericFieldLabel && snippet && snippet.length >= 5) {
      if (cleanText.includes(snippet)) {
        score = Math.max(score, 75)
      } else if (snippet.includes(cleanText) && cleanText.length >= 8) {
        if ((val && cleanText.includes(val)) || (field && cleanText.includes(field))) {
          score = Math.max(score, 75)
        }
      }
    }

    // Domain Synonyms & Compound Labels:

    // 1. "Insured Name and Address" / "Insured Name" / "Insured Address" / "Insured"
    if (cleanText.includes('insured')) {
      const isInsuredEntity = field.includes('insured') || category.includes('insured') || category.includes('employer')
      if (isInsuredEntity) {
        if (cleanText.includes('address') && field.includes('address')) {
          score = Math.max(score, 95)
        } else if (cleanText.includes('name') && field.includes('name')) {
          score = Math.max(score, 95)
        } else {
          score = Math.max(score, 85)
        }
      }
    }

    // 2. "Date of Injury" / "Injury Date" / "Date of Accident" / "Accident Date" / "DOI"
    const isInjuryDateQuery =
      /\b(?:injury|accident|illness|loss|doi)\b/.test(cleanText) && /\b(?:date|day|time|doi)\b/.test(cleanText)
    const isInjuryDateField = /\b(?:injury|accident|illness|loss)\b/.test(field) && /\b(?:date|day)\b/.test(field)
    if (isInjuryDateQuery && isInjuryDateField) {
      score = Math.max(score, 94)
    }

    // 3. "Date of Birth" / "Birth Date" / "DOB"
    const isDobQuery = /\b(?:birth|dob)\b/.test(cleanText)
    const isDobField = /\b(?:birth|dob)\b/.test(field)
    if (isDobQuery && isDobField) {
      score = Math.max(score, 94)
    }

    // 4. "Claim Number" / "Policy Number"
    const isClaimNumberQuery = /\b(?:claim\s+(?:number|no|#)|policy\s+(?:number|no|#))\b/i.test(cleanText)
    const isClaimNumberField = /\b(?:claim\s+(?:number|no|#)|policy\s+(?:number|no|#))\b/i.test(field)
    if (isClaimNumberQuery && isClaimNumberField) {
      score = Math.max(score, 92)
    }

    // 5. "Treating Physician" / "Medical Provider" / "Doctor" / "Treating Facility"
    if (
      !/\b(?:admitted|hospitalized|return\s+to\s+work)\b/i.test(cleanText) &&
      !cleanText.endsWith('?') &&
      /\b(?:treating\s+(?:physician|provider|doctor|facility)|medical\s+provider|attending\s+physician)\b/i.test(
        cleanText,
      )
    ) {
      if (category === 'provider' || /\b(?:physician|provider|doctor|hospital|clinic|facility)\b/.test(field)) {
        score = Math.max(score, 88)
      }
    }

    // 7. "Diagnosis" / "Conditions" / "ICD"
    if (/\b(?:diagnosis|diagnoses|condition|injury description)\b/.test(cleanText)) {
      if (category === 'diagnosis' || /\b(?:diagnosis|diagnoses|condition)\b/.test(field)) {
        score = Math.max(score, 82)
      }
    }

    // 8. "Medication" / "Prescription" / "Pharmacy"
    if (/\b(?:medication|prescribed|pharmacy|rx)\b/.test(cleanText)) {
      if (category === 'medication' || /\b(?:medication|prescribed|pharmacy|rx)\b/.test(field)) {
        score = Math.max(score, 82)
      }
    }

    // 9. "Allergy" / "Allergies"
    if (/\b(?:allergy|allergies|allergic)\b/.test(cleanText)) {
      if (category === 'allergy' || /\b(?:allergy|allergies|allergic)\b/.test(field)) {
        score = Math.max(score, 82)
      }
    }

    if (score > highestScore) {
      highestScore = score
      bestMatch = ent
    }
  }

  return highestScore >= 70 ? bestMatch : null
}
